import { GOOGLE_SCOPES } from "@/constants/Scopes"
import GoogleIcon from "@/assets/logo/google-icon-for-button.svg"
import { GoogleLogin, useGoogleLogin } from "@react-oauth/google"
import { TouchableOpacity, View } from "react-native"
import { jwtDecode } from "jwt-decode"
import { SizeMe } from "react-sizeme"
import BFText from "@/components/standard/BFText"

export interface GoogleCode {
  code?: string | null
  redirectUri?: string
  name: string | null
  idToken?: string | null
  user?: {
    id: string
    name: string | null
    email: string
    photo: string | null
    familyName: string | null
    givenName: string | null
  }
}

export interface GoogleLoginButtonProps {
  children: any
  onLogin: (code: GoogleCode) => void
  disabled?: boolean
  authCode?: boolean
  redirectUri?: string
}

interface GoogleIdToken {
  iss: string
  azp: string
  aud: string
  sub: string
  email: string
  email_verified: boolean
  nbf: number
  name?: string
  picture?: string
  given_name?: string
  family_name?: string
  iat: number
  exp: number
  jti: string
}

export default function GoogleLoginButton({
  onLogin,
  authCode,
  redirectUri = "postmessage",
  children,
}: GoogleLoginButtonProps) {
  const shouldUseRedirectFlow = redirectUri !== "postmessage" && authCode

  const onPress = useGoogleLogin({
    onSuccess: (codeResponse) => {
      onLogin({
        code: codeResponse?.code,
        redirectUri,
        name: null,
      })
    },
    onError: (error) => {
      console.error("Connect Failed:", error)
    },
    onNonOAuthError: (error) => {
      console.error("Connect Failed:", error)
    },
    scope: GOOGLE_SCOPES.join(" "),
    flow: "auth-code",
    ux_mode: shouldUseRedirectFlow ? "redirect" : "popup",
    redirect_uri: shouldUseRedirectFlow ? redirectUri : undefined,
  })

  if (authCode) {
    return (
      <TouchableOpacity
        testID={"google-sign-in-button"}
        className="bg-white w-full h-[50px] rounded-[30px] flex flex-row items-center justify-center"
        onPress={onPress}
      >
        <GoogleIcon width={22} height={22} />
        <BFText className="ml-3">{children}</BFText>
      </TouchableOpacity>
    )
  }

  return (
    <SizeMe>
      {({ size }) => (
        <View className="flex flex-row items-center justify-center w-full">
          <GoogleLogin
            width={size.width || undefined}
            size="large"
            shape="rectangular"
            theme="outline"
            ux_mode="popup"
            onSuccess={(credentialResponse) => {
              if (!credentialResponse.credential) {
                return
              }

              const jwtUserProfile = jwtDecode<GoogleIdToken>(
                credentialResponse.credential
              )
              console.log({ jwtUserProfile })
              onLogin({
                idToken: credentialResponse.credential,
                name: jwtUserProfile.name ?? null,
                user: {
                  id: jwtUserProfile.sub,
                  email: jwtUserProfile.email,
                  givenName: jwtUserProfile.given_name ?? null,
                  familyName: jwtUserProfile.family_name ?? null,
                  name: jwtUserProfile.name ?? null,
                  photo: jwtUserProfile.picture ?? null,
                },
              })
            }}
            onError={() => {
              console.log("Google Login Failed")
            }}
          />
        </View>
      )}
    </SizeMe>
  )
}
